import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ClipboardCheck, Loader2, Lock, Camera, BadgeCheck, ArrowRight, Calendar, MapPin, Euro,
} from "lucide-react";

const DEMANDEUR_TYPES = [
  { v: "particulier", l: "Particulier" },
  { v: "architecte", l: "Architecte" },
  { v: "promoteur", l: "Promoteur" },
  { v: "syndic", l: "Syndic" },
  { v: "bailleur", l: "Bailleur / Foncière" },
  { v: "cmi", l: "CMI" },
  { v: "collectivite", l: "Collectivité" },
  { v: "investisseur", l: "Investisseur" },
  { v: "maitre_oeuvre", l: "Maître d'œuvre" },
  { v: "entreprise_btp", l: "Entreprise BTP" },
];

/**
 * Inline mission-creation form for SEO landing pages.
 *
 * A visitor arriving on /artisan/electricien/orleans can fill this
 * form once and instantly (a) create their demandeur account
 * (b) publish a mission (c) be redirected to their dashboard.
 *
 * Props:
 * - metierLabel (e.g. "Électricien")
 * - metierSlug  (e.g. "electricien")
 * - cityLabel   (e.g. "Orléans")
 * - citySlug    (e.g. "orleans")
 * - context     (e.g. "metier:electricien:orleans" for tracking)
 */
export default function QuickMissionForm({ metierLabel, metierSlug, cityLabel, citySlug, context }) {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | submitting
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    demandeur_type: "particulier",
    title: metierLabel ? `Besoin d'un ${metierLabel.toLowerCase()} à ${cityLabel}` : `Chantier à ${cityLabel}`,
    description: "",
    budget_min: "",
    budget_max: "",
    deadline: "",
    terms_accepted: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) {
      toast.error("Merci d'accepter les CGU pour continuer.");
      return;
    }
    if (form.description.length < 20) {
      toast.error("Décrivez votre projet en au moins 20 caractères.");
      return;
    }
    const budgetMin = Number(form.budget_min);
    const budgetMax = Number(form.budget_max);
    if (!budgetMin || !budgetMax || budgetMin <= 0 || budgetMax < budgetMin) {
      toast.error("Merci d'indiquer un budget cohérent (min ≤ max).");
      return;
    }
    setStatus("submitting");
    try {
      const { data } = await apiClient.post("/public/quick-mission", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        demandeur_type: form.demandeur_type,
        terms_accepted: true,
        title: form.title,
        category: metierSlug || "autres",
        description: form.description,
        budget_min: budgetMin,
        budget_max: budgetMax,
        city: cityLabel,
        deadline: form.deadline || null,
        context_page: context || null,
      });
      toast.success(data.message || "Mission publiée !");
      // Refresh auth session (token cookie already set by backend or use returned token)
      try { await refresh?.(); } catch (_e) { /* ignore refresh error */ }
      // Fall-back: store token if returned
      if (data.token) {
        try { localStorage.setItem("batisur_token", data.token); } catch (_e2) { /* localStorage unavailable */ }
      }
      // Navigate to the mission (fallback to dashboard)
      setTimeout(() => nav(`/dashboard`), 400);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Une erreur est survenue. Merci de réessayer.";
      toast.error(msg);
      setStatus("idle");
    }
  };

  return (
    <form
      onSubmit={submit}
      className="border-2 border-[#0A0A0B] bg-white p-6 lg:p-8 shadow-[6px_6px_0_#FF4D00]"
      data-testid="quick-mission-form"
    >
      <div className="flex items-start gap-3 mb-6">
        <div className="w-12 h-12 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center shrink-0">
          <ClipboardCheck className="w-6 h-6 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">
            // Publier ma demande · gratuit
          </div>
          <div className="display text-2xl leading-tight mt-1">
            Trouvez votre {metierLabel ? metierLabel.toLowerCase() : "artisan"} à {cityLabel} en 5 minutes
          </div>
          <p className="text-sm text-zinc-600 mt-1">
            Décrivez votre chantier, recevez des devis d'artisans SIRET vérifiés et payez sécurisé chez Stripe.
          </p>
        </div>
      </div>

      {/* MISSION SECTION */}
      <div className="mb-6">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">// 1. Votre chantier</div>

        <label className="block mb-4">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Titre du projet *</span>
          <input
            type="text"
            required
            minLength={5}
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
            data-testid="qmf-title"
          />
        </label>

        <label className="block mb-4">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Description détaillée *</span>
          <textarea
            required
            minLength={20}
            rows={4}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Ex. Salle de bain de 6m², prise pour lave-linge, mise aux normes du tableau, quelques luminaires à changer..."
            className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
            data-testid="qmf-description"
          />
          <div className="text-[10px] text-zinc-500 mt-1">Au moins 20 caractères. Plus vous détaillez, plus les devis seront précis.</div>
        </label>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600 flex items-center gap-1">
              <Euro className="w-3 h-3" /> Budget min (€) *
            </span>
            <input
              type="number"
              required
              min={1}
              value={form.budget_min}
              onChange={(e) => set("budget_min", e.target.value)}
              placeholder="500"
              className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
              data-testid="qmf-budget-min"
            />
          </label>
          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600 flex items-center gap-1">
              <Euro className="w-3 h-3" /> Budget max (€) *
            </span>
            <input
              type="number"
              required
              min={1}
              value={form.budget_max}
              onChange={(e) => set("budget_max", e.target.value)}
              placeholder="1500"
              className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
              data-testid="qmf-budget-max"
            />
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Date de début souhaitée
            </span>
            <input
              type="date"
              value={form.deadline}
              onChange={(e) => set("deadline", e.target.value)}
              className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
              data-testid="qmf-deadline"
              min={new Date().toISOString().split("T")[0]}
            />
          </label>
          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Ville
            </span>
            <input
              type="text"
              value={cityLabel}
              readOnly
              className="mt-1 w-full border-2 border-zinc-300 bg-zinc-50 px-3 py-2.5 text-zinc-600"
              data-testid="qmf-city"
            />
          </label>
        </div>
      </div>

      {/* CONTACT SECTION */}
      <div className="mb-6 border-t-2 border-[#0A0A0B] pt-6">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">// 2. Votre profil</div>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Nom complet *</span>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
              data-testid="qmf-name"
            />
          </label>
          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Vous êtes *</span>
            <select
              value={form.demandeur_type}
              onChange={(e) => set("demandeur_type", e.target.value)}
              className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
              data-testid="qmf-demandeur-type"
            >
              {DEMANDEUR_TYPES.map((d) => (
                <option key={d.v} value={d.v}>{d.l}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Email *</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
              data-testid="qmf-email"
            />
          </label>
          <label className="block">
            <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Téléphone *</span>
            <input
              type="tel"
              required
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
              data-testid="qmf-phone"
            />
          </label>
        </div>

        <label className="block mb-4">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Mot de passe * (min 6 caractères)</span>
          <input
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 focus:outline-none focus:bg-[#FFF7ED]"
            data-testid="qmf-password"
          />
          <div className="text-[10px] text-zinc-500 mt-1">Un compte est automatiquement créé pour suivre votre chantier.</div>
        </label>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.terms_accepted}
            onChange={(e) => set("terms_accepted", e.target.checked)}
            className="mt-1 accent-[#FF4D00]"
            data-testid="qmf-terms"
          />
          <span className="text-xs text-zinc-700">
            J'accepte les <a href="/cgu" target="_blank" rel="noreferrer" className="underline text-[#FF4D00]">CGU Batisur</a> et la politique de confidentialité.
          </span>
        </label>
      </div>

      {/* PROTECTIONS */}
      <div className="mb-6 grid grid-cols-3 gap-2 text-center text-[10px] mono uppercase tracking-widest text-zinc-600">
        <div className="border border-zinc-300 py-2"><Lock className="w-3.5 h-3.5 mx-auto mb-1 text-[#FF4D00]" /> Séquestre Stripe</div>
        <div className="border border-zinc-300 py-2"><Camera className="w-3.5 h-3.5 mx-auto mb-1 text-[#FF4D00]" /> Preuves photos</div>
        <div className="border border-zinc-300 py-2"><BadgeCheck className="w-3.5 h-3.5 mx-auto mb-1 text-[#FF4D00]" /> SIRET vérifié</div>
      </div>

      {/* SUBMIT */}
      <button
        type="submit"
        disabled={status === "submitting"}
        className="btn-batisur w-full flex items-center justify-center gap-2 py-4"
        data-testid="qmf-submit"
      >
        {status === "submitting" && <Loader2 className="w-4 h-4 animate-spin" />}
        {status === "submitting" ? "Publication..." : "Publier ma demande gratuitement"}
        <ArrowRight className="w-4 h-4" />
      </button>
      <p className="mt-3 text-[10px] text-center text-zinc-500 uppercase tracking-widest mono">
        Aucune carte requise · Publication instantanée · Devis sous 24-48h
      </p>
    </form>
  );
}
